import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type {
  CompanyStatus,
  PlatformCompanyDetail,
  PlatformCompanyDto,
  PlatformConfigDto,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import DataMigration from './DataMigration';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  onSessionLost: () => void;
}

const STATUS_LABELS: Record<CompanyStatus, string> = {
  trial: 'تجربة',
  active: 'نشطة',
  suspended: 'موقوفة',
};

function statusPill(company: Pick<PlatformCompanyDto, 'status' | 'readOnly'>) {
  // التجربة المنتهية تُعرض بلونها الحقيقى: للقراءة فقط مثل الموقوفة
  const label =
    company.status === 'trial' && company.readOnly
      ? 'انتهت التجربة'
      : STATUS_LABELS[company.status];
  return <span className={'pill' + (company.readOnly ? ' bad' : '')}>{label}</span>;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ar-EG') : '—';
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(days: number): string {
  return isoDay(new Date(Date.now() + days * 86_400_000));
}

function errorText(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const issues = caught.problem.issues ?? [];
    if (issues.length > 0) {
      return issues
        .map((issue) => (issue.path ? issue.path + ': ' : '') + issue.message)
        .join('، ');
    }
    return caught.problem.detail ?? caught.problem.title;
  }
  return caught instanceof Error ? caught.message : String(caught);
}

type Screen =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'detail'; id: string }
  | { kind: 'migration'; id: string; name: string };

/**
 * لوحة المنصة — لمشغّل المنصة المشتركة وحده (الـ API يرد 404 لغيره). تعرض الشركات
 * واشتراكها ومؤشرات نشاطها، **ولا تعرض أى رقم مالى**: المشغّل ليس عضوًا فى الشركات.
 */
export default function Platform({ onSessionLost }: Props) {
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });
  const [companies, setCompanies] = useState<PlatformCompanyDto[] | null>(null);
  const [config, setConfig] = useState<PlatformConfigDto | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(errorText(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    api.platformCompanies().then(setCompanies).catch(handleError);
    api.platformConfig().then(setConfig).catch(handleError);
  }, [handleError]);

  useEffect(load, [load]);

  return (
    <>
      <h1>لوحة المنصة</h1>
      <p className="sub">
        الشركات المستضافة على هذا الخادم واشتراكها. الإيقاف يمنع الإضافة والتعديل فقط — الدفاتر تبقى
        مقروءة وقابلة للتصدير، ولا يُحذف شىء.
      </p>
      {failure && <p className="error">{failure}</p>}

      {screen.kind === 'list' && (
        <div className="panel">
          <div className="panel-head">
            <h2>الشركات</h2>
            <button type="button" className="primary" onClick={() => setScreen({ kind: 'create' })}>
              إضافة شركة
            </button>
          </div>
          {!companies && <Loading />}
          {companies && companies.length === 0 && <p className="sub">لا توجد شركات بعد.</p>}
          {companies && companies.length > 0 && (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>الشركة</th>
                    <th>الحالة</th>
                    <th>الباقة</th>
                    <th>المستخدمون</th>
                    <th>القيود</th>
                    <th>آخر نشاط</th>
                    <th>نهاية التجربة</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr
                      key={company.id}
                      className="clickable"
                      tabIndex={0}
                      onClick={() => setScreen({ kind: 'detail', id: company.id })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') setScreen({ kind: 'detail', id: company.id });
                      }}
                    >
                      <td>{company.legalNameAr}</td>
                      <td>{statusPill(company)}</td>
                      <td className="mono">{company.plan ?? '—'}</td>
                      <td className="num-cell">
                        {company.userCount}
                        {company.maxUsers ? ' / ' + company.maxUsers : ''}
                      </td>
                      <td className="num-cell">{company.journalCount}</td>
                      <td>{formatDate(company.lastActivityAt)}</td>
                      <td>{company.status === 'trial' ? formatDate(company.trialEndsAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {screen.kind === 'create' && config && (
        <CreateCompany
          config={config}
          onCancel={() => setScreen({ kind: 'list' })}
          onCreated={(company) => {
            load();
            setScreen({ kind: 'detail', id: company.id });
          }}
          onError={handleError}
        />
      )}

      {screen.kind === 'detail' && (
        <CompanyDetail
          key={screen.id}
          id={screen.id}
          plans={config?.plans ?? []}
          onBack={() => {
            load();
            setScreen({ kind: 'list' });
          }}
          onMigration={(name) => setScreen({ kind: 'migration', id: screen.id, name })}
          onError={handleError}
        />
      )}

      {screen.kind === 'migration' && (
        <DataMigration
          key={screen.id}
          companyId={screen.id}
          companyName={screen.name}
          asPlatform
          onSessionLost={onSessionLost}
          onBack={() => setScreen({ kind: 'detail', id: screen.id })}
        />
      )}
    </>
  );
}

function CreateCompany({
  config,
  onCancel,
  onCreated,
  onError,
}: {
  config: PlatformConfigDto;
  onCancel: () => void;
  onCreated: (company: PlatformCompanyDetail) => void;
  onError: (caught: unknown) => void;
}) {
  const year = new Date().getFullYear();
  const [legalNameAr, setLegalNameAr] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [fiscalYearStart, setFiscalYearStart] = useState(year + '-01-01');
  const [fiscalYearEnd, setFiscalYearEnd] = useState(year + '-12-31');
  const [status, setStatus] = useState<'trial' | 'active'>('trial');
  const [trialEndsAt, setTrialEndsAt] = useState(addDays(config.trialDays));
  const [plan, setPlan] = useState('');
  const [maxUsers, setMaxUsers] = useState(
    config.defaultMaxUsers ? String(config.defaultMaxUsers) : '',
  );
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    try {
      onCreated(
        await api.createPlatformCompany({
          legalNameAr: legalNameAr.trim(),
          taxRegistrationNumber: taxNumber.trim() || null,
          currency: 'EGP',
          fiscalYearStart,
          fiscalYearEnd,
          status,
          trialEndsAt: status === 'trial' ? trialEndsAt : null,
          plan: plan.trim() || null,
          maxUsers: maxUsers.trim() ? Number(maxUsers) : null,
          owner: {
            displayName: ownerName.trim(),
            email: ownerEmail.trim(),
            password: ownerPassword,
          },
        }),
      );
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
      <h2>إضافة شركة</h2>
      <p className="sub">
        تُنشأ الشركة بدليل الحسابات المعتمد وسنة مالية مفتوحة وخزينة وحساب بنكى — مثل التسطيبة
        المنفصلة تمامًا. أنت لا تصير عضوًا فيها ولا ترى دفاترها.
      </p>
      <div className="form-row">
        <label className="grow">
          اسم الشركة
          <input
            value={legalNameAr}
            maxLength={200}
            onChange={(event) => setLegalNameAr(event.target.value)}
          />
          <Hint>الاسم القانونى للشركة العميلة كما فى سجلها التجارى.</Hint>
        </label>
        <label>
          الرقم الضريبى (اختيارى)
          <input
            dir="ltr"
            value={taxNumber}
            maxLength={32}
            onChange={(event) => setTaxNumber(event.target.value)}
          />
        </label>
      </div>
      <div className="form-row">
        <label>
          بداية السنة المالية
          <input
            type="date"
            value={fiscalYearStart}
            onChange={(event) => setFiscalYearStart(event.target.value)}
          />
          <Hint>لمعظم الشركات المصرية: ١ يناير.</Hint>
        </label>
        <label>
          نهايتها
          <input
            type="date"
            value={fiscalYearEnd}
            onChange={(event) => setFiscalYearEnd(event.target.value)}
          />
          <Hint>لمعظم الشركات المصرية: ٣١ ديسمبر.</Hint>
        </label>
      </div>

      <h3>الاشتراك</h3>
      <div className="form-row">
        <label>
          الحالة
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as 'trial' | 'active')}
          >
            <option value="trial">تجربة</option>
            <option value="active">نشطة</option>
          </select>
          <Hint>«تجربة» لفترة محددة تنتهى فى «آخر يوم فى التجربة»، و«نشط» لاشتراك قائم.</Hint>
        </label>
        {status === 'trial' && (
          <label>
            آخر يوم فى التجربة
            <input
              type="date"
              value={trialEndsAt}
              onChange={(event) => setTrialEndsAt(event.target.value)}
            />
          </label>
        )}
        <label>
          الباقة
          <input
            dir="ltr"
            list="platform-plans"
            value={plan}
            maxLength={32}
            placeholder="basic"
            onChange={(event) => setPlan(event.target.value)}
          />
          <datalist id="platform-plans">
            {config.plans.map((code) => (
              <option key={code} value={code} />
            ))}
          </datalist>
          <Hint>اسم الباقة المتفق عليها مع العميل.</Hint>
        </label>
        <label>
          أقصى عدد للمستخدمين
          <input
            type="number"
            min={1}
            value={maxUsers}
            placeholder="بلا سقف"
            onChange={(event) => setMaxUsers(event.target.value)}
          />
          <Hint>كم شخصًا يستطيع الدخول على هذه الشركة. اتركه فارغًا لعدد غير محدود.</Hint>
        </label>
      </div>

      <h3>المالك الأول</h3>
      <div className="form-row">
        <label className="grow">
          الاسم
          <input
            value={ownerName}
            maxLength={100}
            onChange={(event) => setOwnerName(event.target.value)}
          />
        </label>
        <label className="grow">
          البريد
          <input
            type="email"
            dir="ltr"
            value={ownerEmail}
            onChange={(event) => setOwnerEmail(event.target.value)}
          />
        </label>
        <label className="grow">
          كلمة سر أولى (١٢ محرفًا على الأقل)
          <input
            type="password"
            autoComplete="new-password"
            value={ownerPassword}
            onChange={(event) => setOwnerPassword(event.target.value)}
          />
          <Hint>أعطها لصاحبها بطريقة آمنة (لا فى جروب)، ويستطيع تغييرها بعد أول دخول.</Hint>
        </label>
      </div>
      <p className="sub">
        سلّم كلمة السر للعميل بنفسك. أول دخول له يطلب تفعيل التحقق بخطوتين، ثم يغيّر كلمة السر من
        الإعدادات.
      </p>
      <div className="form-actions">
        <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
          رجوع
        </button>
        <button
          type="submit"
          className="primary"
          disabled={busy || !legalNameAr.trim() || !ownerEmail.trim() || !ownerName.trim()}
        >
          إنشاء الشركة
        </button>
      </div>
    </form>
  );
}

function CompanyDetail({
  id,
  plans,
  onBack,
  onMigration,
  onError,
}: {
  id: string;
  plans: readonly string[];
  onBack: () => void;
  /** فتح مركز ترحيل البيانات للشركة كخدمة — كل خطوة تُسجَّل باسم المشغّل. */
  onMigration: (companyName: string) => void;
  onError: (caught: unknown) => void;
}) {
  const [company, setCompany] = useState<PlatformCompanyDetail | null>(null);
  const [plan, setPlan] = useState('');
  const [maxUsers, setMaxUsers] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const show = useCallback((loaded: PlatformCompanyDetail) => {
    setCompany(loaded);
    setPlan(loaded.plan ?? '');
    setMaxUsers(loaded.maxUsers ? String(loaded.maxUsers) : '');
    setTrialEndsAt(loaded.trialEndsAt ? loaded.trialEndsAt.slice(0, 10) : '');
  }, []);

  useEffect(() => {
    api.platformCompany(id).then(show).catch(onError);
  }, [id, show, onError]);

  async function run(action: () => Promise<PlatformCompanyDetail>): Promise<void> {
    setBusy(true);
    try {
      show(await action());
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (!company) {
    return <Loading />;
  }

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>
            {company.legalNameAr} {statusPill(company)}
          </h2>
          <div className="panel-actions">
            <button
              type="button"
              className="ghost"
              onClick={() => onMigration(company.legalNameAr)}
              title="ترحيل بيانات العميل من برنامجه السابق كخدمة — التدقيق باسمك"
            >
              ترحيل البيانات كخدمة
            </button>
            <button type="button" className="ghost" onClick={onBack}>
              كل الشركات
            </button>
          </div>
        </div>
        <div className="cards">
          <div className="card">
            <div className="label">المستخدمون</div>
            <div className="num">
              {company.userCount}
              {company.maxUsers ? ' / ' + company.maxUsers : ''}
            </div>
          </div>
          <div className="card">
            <div className="label">القيود المنشورة</div>
            <div className="num">{company.journalCount}</div>
          </div>
          <div className="card">
            <div className="label">آخر نشاط</div>
            <div className="num">{formatDate(company.lastActivityAt)}</div>
          </div>
          <div className="card">
            <div className="label">أُنشئت</div>
            <div className="num">{formatDate(company.createdAt)}</div>
          </div>
        </div>
        {company.status === 'suspended' && (
          <p className="notice">
            موقوفة منذ {formatDate(company.suspendedAt)}: {company.suspendedReason}
          </p>
        )}
        <h3>المُلّاك</h3>
        <table>
          <thead>
            <tr>
              <th>الاسم</th>
              <th>البريد</th>
              <th>آخر دخول</th>
            </tr>
          </thead>
          <tbody>
            {company.owners.map((owner) => (
              <tr key={owner.userId}>
                <td>{owner.displayName}</td>
                <td className="mono">{owner.email}</td>
                <td>{formatDate(owner.lastLoginAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form
        className="panel"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void run(() =>
            api.updatePlatformCompany(id, {
              plan: plan.trim() || null,
              maxUsers: maxUsers.trim() ? Number(maxUsers) : null,
              ...(company.status === 'trial' && trialEndsAt ? { trialEndsAt } : {}),
            }),
          );
        }}
      >
        <h2>الباقة والسقف</h2>
        <div className="form-row">
          <label>
            الباقة
            <input
              dir="ltr"
              list="platform-plans-detail"
              value={plan}
              maxLength={32}
              onChange={(event) => setPlan(event.target.value)}
            />
            <datalist id="platform-plans-detail">
              {plans.map((code) => (
                <option key={code} value={code} />
              ))}
            </datalist>
            <Hint>اسم الباقة المتفق عليها مع العميل.</Hint>
          </label>
          <label>
            أقصى عدد للمستخدمين
            <input
              type="number"
              min={1}
              value={maxUsers}
              placeholder="بلا سقف"
              onChange={(event) => setMaxUsers(event.target.value)}
            />
            <Hint>كم شخصًا يستطيع الدخول على هذه الشركة. اتركه فارغًا لعدد غير محدود.</Hint>
          </label>
          {company.status === 'trial' && (
            <label>
              آخر يوم فى التجربة
              <input
                type="date"
                value={trialEndsAt}
                onChange={(event) => setTrialEndsAt(event.target.value)}
              />
            </label>
          )}
        </div>
        <p className="sub">خفض السقف لا يحذف مستخدمًا قائمًا — يمنع إضافة الجديد فقط.</p>
        <div className="form-actions">
          <button type="submit" className="primary" disabled={busy}>
            حفظ
          </button>
        </div>
      </form>

      <div className="panel">
        <h2>{company.status === 'suspended' ? 'إعادة التفعيل' : 'الحالة'}</h2>
        {company.status === 'suspended' ? (
          <div className="form-actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void run(() => api.reactivateCompany(id, { status: 'active' }))}
            >
              تفعيل الشركة
            </button>
          </div>
        ) : (
          <>
            {company.status === 'trial' && (
              <div className="form-actions">
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() => void run(() => api.reactivateCompany(id, { status: 'active' }))}
                >
                  تحويل التجربة إلى اشتراك نشط
                </button>
              </div>
            )}
            <div className="form-row">
              <label className="grow">
                سبب الإيقاف (يظهر فى سجل التدقيق)
                <input
                  value={reason}
                  maxLength={500}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
            </div>
            <p className="sub">
              الإيقاف يمنع كل إضافة وتعديل فى الشركة. القراءة والتقارير والتصدير تبقى متاحة
              لأعضائها.
            </p>
            <div className="form-actions">
              <button
                type="button"
                className="ghost"
                disabled={busy || !reason.trim()}
                onClick={() => {
                  if (!window.confirm('إيقاف ' + company.legalNameAr + '؟')) return;
                  void run(() => api.suspendCompany(id, { reason: reason.trim() }));
                }}
              >
                إيقاف الشركة
              </button>
              <Hint block>
                يمنع مستخدمى الشركة من الدخول، وبياناتها تبقى محفوظة كما هى ويمكن إعادة تفعيلها.
              </Hint>
            </div>
          </>
        )}
      </div>
    </>
  );
}
